import { Check, ChevronDown, Save01, ShieldPlus, XClose } from "@untitledui/icons";
import type { ComponentType } from "react";
import { Button } from "@/components/base/buttons/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cx } from "@/utils/cx";

const RiskActionGroupLabel = ({ label }: { label: string }) => (
  <DropdownMenuLabel className="px-3 pt-2 pb-1 text-xs font-medium text-quaternary">{label}</DropdownMenuLabel>
);

const RiskActionMenuItem = ({
  icon: Icon,
  label,
  description,
  onSelect,
}: {
  icon: ComponentType<{ className?: string }>;
  label: string;
  description?: string;
  onSelect: () => void;
}) => (
  <DropdownMenuItem
    onClick={(e) => {
      e.stopPropagation();
      onSelect();
    }}
    className={cx("flex gap-2.5 px-3", description ? "items-start py-2.5" : "items-center py-2")}
  >
    <Icon className={cx("w-4 h-4 text-fg-quaternary shrink-0", description && "mt-1")} />
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-secondary font-semibold">{label}</span>
      {description && <span className="text-xs font-normal text-tertiary">{description}</span>}
    </div>
  </DropdownMenuItem>
);

export const RisksActionsDropdown = ({
  onConfirm,
  onConfirmWithoutPolicies,
  onDismiss,
  onAddRisk,
  disabled,
}: {
  onConfirm: () => void;
  onConfirmWithoutPolicies: () => void;
  onDismiss: () => void;
  onAddRisk: () => void;
  disabled?: boolean;
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild disabled={disabled}>
      <Button isDisabled={disabled} iconTrailing={<ChevronDown className="w-4 h-4" />}>
        Risks
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={8} className="w-[268px] px-0">
      <RiskActionGroupLabel label="These risks are correct" />
      <RiskActionMenuItem
        icon={Check}
        label="Confirm risks"
        description="Runs the safety policy set for each risk"
        onSelect={onConfirm}
      />
      <RiskActionMenuItem
        icon={Save01}
        label="Confirm without running policies"
        description="Overrides the safety configuration and marks as no action needed"
        onSelect={onConfirmWithoutPolicies}
      />
      <DropdownMenuSeparator />
      <RiskActionGroupLabel label="These risks are wrong" />
      <RiskActionMenuItem
        icon={XClose}
        label="Dismiss risks"
        description="Clears them from review, nothing runs"
        onSelect={onDismiss}
      />
      <DropdownMenuSeparator />
      <RiskActionMenuItem icon={ShieldPlus} label="Add a risk" onSelect={onAddRisk} />
    </DropdownMenuContent>
  </DropdownMenu>
);
